/** @jsxImportSource @emotion/react */
import { Drawer } from "./Drawer";
import { ProductListItem } from "../module/products/models/product";
import * as authSelectors from "../module/auth/authSelectors";
import { ProductsFilter } from "../module/products/ProductsFilter";
import React, { useState } from "react";
import _ from "lodash";
import { css } from "@emotion/react";
import { SearchTextFieldWithKeyboardInteraction } from "./forms/fields/SearchTextFieldWithKeyboardInteraction";
import { useListProducts_ } from "../module/products/service/products-api";
import { useSelector } from "react-redux";
import { useSearchParamsManipulation } from "../hooks/use-searchparams-manipulation";
import { CheckboxField } from "./forms/fields/CheckboxField";
import { ButtonAction, ButtonPrimary } from "./NewButtons";
import { SpinnerBlock } from "../module/common/Spinner";
import { ActionSpinner } from "./ActionSpinner";
import { useHandleFetchError } from "../error-handler";
import { EmptyState } from "./EmptyState";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (products: ProductListItem[]) => void;
  filters: { classification: string };
  productIdsToLeaveOut?: string[];
  buttonText: string;
  isLoadingSave?: boolean;
  modalHeader: string;
  includeClassificationFilter?: boolean;
  filterProductsFunction?: (products: ProductListItem[]) => ProductListItem[];
};

const bodyWrapperStyling = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SelectProductsDrawer = ({
  isOpen,
  onClose,
  onSave,
  filters,
  productIdsToLeaveOut,
  buttonText,
  modalHeader,
  isLoadingSave,
  includeClassificationFilter,
  filterProductsFunction,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [products, setProducts] = useState<string[]>([]);
  const {
    searchValue,
    setSearchValue,
    removeSearchValue,
    categories,
    tags,
    kitchenCategories,
    currency,
    country,
  } = useSearchParamsManipulation();
  const debouncedSetSearchValue = _.debounce(setSearchValue, 400);

  const handleClose = () => {
    onClose();
    setProducts([]);
  };

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useListProducts_(
    organizationKeyName,
    {
      ...(filters.classification && { classification: filters.classification }),
      tags: tags?.split(",") || [],
      productCategory: categories?.split(",") || [],
      kitchenCategory: kitchenCategories?.split(",") || [],
      currencies: currency?.split(",") || [],
      countryAlpha2Codes: country?.split(",") || [],
      ...(searchValue && { name: searchValue }),
    },
    isOpen
  );

  const fallbackFilterFn = (products: ProductListItem[]) => {
    return products;
  };

  const filterFn = filterProductsFunction ?? fallbackFilterFn;

  // Cancelling the debounced functions when the component unmounts
  React.useEffect(() => {
    return () => {
      debouncedSetSearchValue.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useHandleFetchError(error);

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      title={modalHeader}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingSave}
          onClick={() => {
            let productsToAdd: ProductListItem[] = [];
            if (data?.pages) {
              data.pages.forEach((page) => {
                page.data.forEach((product) => {
                  if (products.includes(product.id)) {
                    productsToAdd.push(product);
                  }
                });
              });
            }
            onSave(productsToAdd);
            handleClose();
          }}
        >
          {isLoadingSave ? <ActionSpinner /> : buttonText}
        </ButtonAction>
      }
    >
      <SearchTextFieldWithKeyboardInteraction
        searchValue={searchValue || ""}
        isDefaultValues={true}
        setSearchValue={(value) => {
          debouncedSetSearchValue.cancel();
          if (value === "") {
            removeSearchValue();
          } else {
            debouncedSetSearchValue(value);
          }
        }}
        placeholder="Search for product..."
      />
      <ProductsFilter
        includeClassificationFilter={includeClassificationFilter}
      />
      <div css={bodyWrapperStyling}>
        {isLoading ? (
          <SpinnerBlock />
        ) : (
          <>
            {(data?.pages && data.pages.length === 0) ||
            (data?.pages?.length === 1 && data.pages[0].data.length === 0) ? (
              <EmptyState
                title="No products found"
                subtitle="Try another filter"
              />
            ) : (
              <>
                {data?.pages.map((page) =>
                  filterFn(
                    page.data.sort((a, b) => a.name.localeCompare(b.name))
                  )
                    .filter((p) => {
                      if (
                        productIdsToLeaveOut &&
                        productIdsToLeaveOut.length > 0
                      ) {
                        return !productIdsToLeaveOut.includes(p.id);
                      }
                      return true;
                    })
                    .map((product) => (
                      <div key={product.id}>
                        <CheckboxField
                          label={product.name}
                          checked={products.includes(product.id)}
                          onChange={() => {
                            setProducts([...products, product.id]);
                          }}
                        />
                      </div>
                    ))
                )}
                {hasNextPage && (
                  <ButtonPrimary
                    disabled={isFetchingNextPage}
                    onClick={() => {
                      fetchNextPage();
                    }}
                  >
                    {isFetchingNextPage ? (
                      <ActionSpinner />
                    ) : (
                      "Load more products"
                    )}
                  </ButtonPrimary>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};
